import keyTermRegex from "./keyTermRegex";

/**
 * Accepts an array of objects and a string for comparison. First we filter the array down to only content
 * objects that are paragraphs, as only paragraphs will contain power words. Then for each remaining paragraph,
 * we check whether a 'powerWord_block' or 'keyTerm_block' object exists with the text field that matches the keyTerm.
 * @param {string} keyTerm
 * @param {array} rawTextContent
 * @returns {boolean}
 */
const powerWordExists = (keyTerm, rawTextContent) => {
  if (!keyTerm || !rawTextContent) return false;

  let matchFound = false;

  const paragraphsWithPowerWords = rawTextContent.filter((contentBlock) => {
    return (
      contentBlock.type === "paragraph" &&
      contentBlock?.content?.some((item) =>
        ["powerWord_block", "keyTerm_block"].includes(item.type),
      )
    );
  });

  paragraphsWithPowerWords.forEach((paragraph) => {
    paragraph?.content.forEach((contentItem) => {
      if (["powerWord_block", "keyTerm_block"].includes(contentItem.type)) {
        if (
          contentItem?.content?.some((item) =>
            item.text.toLowerCase().startsWith(keyTerm),
          )
        ) {
          matchFound = true;
        }
      }
    });
  });

  return matchFound;
};

/**
 * Each targetObject contains a 'text' field which will contain a substring that matches the keyTerm.
 * The string in the text field is split using regex that matches the first instance of the subtring, case-insensitive and uses it
 * as a delimiter to split the string into an array. The element in the array that is equal to the key term is replaced by a keyterm_block.
 * This also uses captures instances of the substring if found in the larger string with alphabetical characters immediately following.
 * For example, if our term is 'capybara' but the first instance of the term is 'capybaras', it will split on 'capybaras' and return that as the key term.
 * All other elements become copies of the initial targetObject with modified text.
 * @param {string} keyTerm
 * @param {object} targetObject
 * @returns {array}
 */
const insertKeyTerm = (keyTerm, targetObject, regexPattern) => {
  const textArray = targetObject.text
    .split(regexPattern)
    .filter((text) => text.length > 0);

  const updatedArray = textArray.map((text) => {
    const keyTermBlock = {
      type: "keyTerm_block",
      attrs: {
        definition: keyTerm.definition.trim(),
      },
      content: [
        {
          text,
          type: "text",
        },
      ],
    };

    if (text.toLowerCase().startsWith(keyTerm.term)) {
      return keyTermBlock;
    } else {
      const targetCopy = {
        ...targetObject,
        text,
      };

      return targetCopy;
    }
  });

  return updatedArray;
};

/**
 * For each keyTerm object, we check the rawText content to find a nested match. If the match is in a text string,
 * the key term is added as a power word, with the string having been broken up into new text objects. If the match is already a power
 * word, the key term is not added. This latter behavior covers existing power words as well as key terms that are added in this function.
 * @param {object} rawText
 * @param {array} keyTerms
 * @returns {object}
 */
const addKeyTermsToContentText = (rawText, keyTerms) => {
  if (!keyTerms) return rawText;
  if (!rawText) return;

  const rawTextContent = rawText.content;

  for (const keyTerm of keyTerms) {
    for (const contentBlock of rawTextContent) {
      if (
        contentBlock.type === "paragraph" &&
        !powerWordExists(keyTerm.term, rawTextContent)
      ) {
        const content = contentBlock.content;
        if (content) {
          for (let i = 0; i < content.length; i++) {
            const regexPattern = keyTermRegex(keyTerm.term);
            if (content[i].text?.match(regexPattern)) {
              const newTextBlocks = insertKeyTerm(
                keyTerm,
                content[i],
                regexPattern,
              );
              content.splice(i, 1, ...newTextBlocks);
              break;
            }
          }
        }
      }
    }
  }

  return { ...rawText, content: rawTextContent };
};

export default addKeyTermsToContentText;
